import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { DEMO_MANIFEST_URL, REPORT_TABS } from "../../data/immerselyDemoData";
import "./ImmerselyDemo.scss";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

function getQuadrant(arousal, valence) {
  if (arousal >= 0 && valence >= 0) return { label: "Engaged", emoji: "🔥" };
  if (arousal >= 0 && valence < 0) return { label: "Frustrated", emoji: "⚠️" };
  if (arousal < 0 && valence >= 0) return { label: "Calm", emoji: "🙂" };
  return { label: "Disengaged", emoji: "😴" };
}

function findWindowAtTime(windows, seconds) {
  if (!windows?.length) return null;
  return (
    windows.find((w) => seconds >= w.startSecond && seconds < w.endSecond) ||
    windows[windows.length - 1]
  );
}

function chartDataForTimeline(timeline, selectedSecond) {
  return {
    labels: timeline.map((point) => `${point.t}s`),
    datasets: [
      {
        label: "Arousal",
        data: timeline.map((point) => point.arousal),
        borderColor: "#2dd4bf",
        backgroundColor: "rgba(45, 212, 191, 0.22)",
        tension: 0.35,
        pointRadius: timeline.map((point) => (point.t === selectedSecond ? 6 : 2)),
      },
      {
        label: "Valence",
        data: timeline.map((point) => point.valence),
        borderColor: "#fb7185",
        backgroundColor: "rgba(251, 113, 133, 0.22)",
        tension: 0.35,
        pointRadius: timeline.map((point) => (point.t === selectedSecond ? 6 : 2)),
      },
    ],
  };
}

function VaQuadrant({ arousal, valence }) {
  const padding = 12;
  const size = 180;
  const inner = size - padding * 2;
  const x = padding + ((valence + 1) / 2) * inner;
  const y = padding + ((1 - (arousal + 1) / 2)) * inner;

  return (
    <svg className="va-quadrant" viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
      <rect x={padding} y={padding} width={inner} height={inner} fill="rgba(8,12,28,0.6)" stroke="rgba(159,176,217,0.35)" />
      <line x1={size / 2} y1={padding} x2={size / 2} y2={size - padding} stroke="rgba(159,176,217,0.25)" />
      <line x1={padding} y1={size / 2} x2={size - padding} y2={size / 2} stroke="rgba(159,176,217,0.25)" />
      <text x={padding + 4} y={padding + 12} fill="#9fb0d9" fontSize="9">
        Calm
      </text>
      <text x={size - padding - 34} y={padding + 12} fill="#9fb0d9" fontSize="9">
        Happy
      </text>
      <text x={padding + 4} y={size - padding - 4} fill="#9fb0d9" fontSize="9">
        Bored
      </text>
      <text x={size - padding - 36} y={size - padding - 4} fill="#9fb0d9" fontSize="9">
        Upset
      </text>
      <circle cx={x} cy={y} r="7" fill="#5ea2ff" stroke="#eef3ff" strokeWidth="2" />
    </svg>
  );
}

export default function ImmerselyDemo() {
  const videoRef = useRef(null);
  const virtualTimerRef = useRef(null);

  const [manifest, setManifest] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [activeDatasetId, setActiveDatasetId] = useState(null);
  const [activeReportId, setActiveReportId] = useState("behavioral-analysis-report");
  const [selectedMomentId, setSelectedMomentId] = useState(null);
  const [currentSecond, setCurrentSecond] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasGameplayVideo, setHasGameplayVideo] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    fetch(DEMO_MANIFEST_URL)
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to load demo manifest (${res.status})`);
        return res.json();
      })
      .then((data) => {
        setManifest(data);
        if (data.datasets?.length) {
          setActiveDatasetId(data.datasets[0].id);
          setSelectedMomentId(data.datasets[0].keyMoments?.[0]?.id || null);
        }
      })
      .catch((err) => setLoadError(err.message || "Failed to load offline demo pack"));
  }, []);

  const activeDataset = useMemo(
    () => manifest?.datasets?.find((d) => d.id === activeDatasetId) || null,
    [manifest, activeDatasetId]
  );

  const windowSeries = useMemo(() => activeDataset?.timeline || [], [activeDataset]);

  const emotionWindows = useMemo(() => {
    if (!activeDataset) return [];
    return (activeDataset.timeline || []).map((point, idx, arr) => ({
      startSecond: point.t,
      endSecond: arr[idx + 1]?.t ?? point.t + 5,
      arousal: point.arousal,
      valence: point.valence,
      emotionLabel: point.emotionLabel || "neutral",
    }));
  }, [activeDataset]);

  const currentEmotion = useMemo(() => {
    const win = findWindowAtTime(emotionWindows, currentSecond);
    return win || { arousal: 0, valence: 0, emotionLabel: "neutral" };
  }, [emotionWindows, currentSecond]);

  const quadrant = getQuadrant(currentEmotion.arousal, currentEmotion.valence);

  const selectedMoment = useMemo(() => {
    if (!activeDataset?.keyMoments?.length) return null;
    return (
      activeDataset.keyMoments.find((m) => m.id === selectedMomentId) || activeDataset.keyMoments[0]
    );
  }, [activeDataset, selectedMomentId]);

  const availableReportTabs = useMemo(() => {
    if (!activeDataset?.reports) return [];
    return REPORT_TABS.filter((tab) => activeDataset.reports[tab.id]);
  }, [activeDataset]);

  const activeReportUrl = activeDataset?.reports?.[activeReportId] || null;

  const chartData = useMemo(
    () => chartDataForTimeline(windowSeries, currentSecond),
    [windowSeries, currentSecond]
  );

  const seekTo = useCallback(
    (seconds) => {
      const clamped = Math.max(0, Math.min(seconds, (activeDataset?.durationSeconds || 120) - 0.1));
      setCurrentSecond(Math.floor(clamped));
      if (videoRef.current && hasGameplayVideo) {
        videoRef.current.currentTime = clamped;
      }
    },
    [activeDataset, hasGameplayVideo]
  );

  const stopVirtualPlayback = useCallback(() => {
    if (virtualTimerRef.current) {
      clearInterval(virtualTimerRef.current);
      virtualTimerRef.current = null;
    }
  }, []);

  const startVirtualPlayback = useCallback(() => {
    stopVirtualPlayback();
    virtualTimerRef.current = setInterval(() => {
      setCurrentSecond((prev) => {
        const max = (activeDataset?.durationSeconds || 120) - 1;
        if (prev >= max) {
          stopVirtualPlayback();
          setIsPlaying(false);
          return 0;
        }
        return prev + 1;
      });
    }, 1000);
  }, [activeDataset, stopVirtualPlayback]);

  useEffect(() => {
    if (!activeDataset) return;
    const firstMoment = activeDataset.keyMoments?.[0];
    setSelectedMomentId(firstMoment?.id || null);
    setCurrentSecond(firstMoment?.startSeconds || 0);
    setActiveReportId(
      availableReportTabs[0]?.id || "behavioral-analysis-report"
    );
    setHasGameplayVideo(true);
    stopVirtualPlayback();
    setIsPlaying(false);
  }, [activeDatasetId, activeDataset, availableReportTabs, stopVirtualPlayback]);

  useEffect(() => {
    if (isPlaying && !hasGameplayVideo) {
      startVirtualPlayback();
    } else {
      stopVirtualPlayback();
    }
    return stopVirtualPlayback;
  }, [isPlaying, hasGameplayVideo, startVirtualPlayback, stopVirtualPlayback]);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "Escape" && document.fullscreenElement) {
        document.exitFullscreen();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const handleChartClick = (_, elements) => {
    if (!elements.length || !windowSeries.length) return;
    const idx = elements[0].index;
    const second = windowSeries[idx]?.t;
    if (second != null) seekTo(second);
  };

  const togglePlay = () => {
    if (hasGameplayVideo && videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play();
        setIsPlaying(true);
      } else {
        videoRef.current.pause();
        setIsPlaying(false);
      }
      return;
    }
    setIsPlaying((prev) => !prev);
  };

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch {
      // Fullscreen may be blocked in some contexts.
    }
  };

  const resetDemo = () => {
    stopVirtualPlayback();
    setIsPlaying(false);
    seekTo(activeDataset?.segmentStart || 0);
  };

  if (loadError) {
    return (
      <div className="immersely-demo-page">
        <div className="demo-error">
          <h1>Offline demo unavailable</h1>
          <p>{loadError}</p>
          <p>
            Run <code>python scripts/build-demo-pack.py</code> then rebuild the app.
          </p>
        </div>
      </div>
    );
  }

  if (!manifest || !activeDataset) {
    return (
      <div className="immersely-demo-page">
        <div className="demo-loading">Loading offline demo pack…</div>
      </div>
    );
  }

  return (
    <div className={`immersely-demo-page ${isFullscreen ? "is-fullscreen" : ""}`}>
      <header className="demo-header">
        <div>
          <h1>Immersely Playtest Intelligence</h1>
          <p>
            Real session replay — gameplay synced to emotion signals (no faces). Fully offline.
          </p>
        </div>
        <div className="header-actions">
          <button type="button" className="ghost-btn" onClick={toggleFullscreen}>
            {isFullscreen ? "Exit fullscreen" : "Fullscreen"}
          </button>
          <button type="button" className="ghost-btn" onClick={resetDemo}>
            Reset
          </button>
        </div>
      </header>

      <section className="dataset-switcher">
        {manifest.datasets.map((dataset) => (
          <button
            key={dataset.id}
            type="button"
            className={`dataset-tab ${dataset.id === activeDatasetId ? "active" : ""}`}
            onClick={() => setActiveDatasetId(dataset.id)}
          >
            {dataset.label}
          </button>
        ))}
      </section>

      <section className="kpi-strip">
        {activeDataset.quickKpis?.map((kpi) => (
          <article key={kpi.label} className="kpi-card">
            <span>{kpi.label}</span>
            <strong>{kpi.value}</strong>
          </article>
        ))}
        <article className="kpi-card">
          <span>Session</span>
          <strong>{activeDataset.sessionRef}</strong>
        </article>
      </section>

      <section className="synced-showcase">
        <div className="gameplay-panel panel">
          <div className="panel-head">
            <h3>Gameplay replay</h3>
            <span>{currentSecond}s / {activeDataset.durationSeconds}s</span>
          </div>

          {hasGameplayVideo ? (
            <video
              ref={videoRef}
              className="gameplay-video"
              src={activeDataset.gameplayUrl}
              controls={false}
              playsInline
              preload="metadata"
              onError={() => setHasGameplayVideo(false)}
              onTimeUpdate={(event) => setCurrentSecond(Math.floor(event.target.currentTime))}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onEnded={() => {
                setIsPlaying(false);
                seekTo(0);
              }}
            />
          ) : (
            <div className="gameplay-placeholder">
              <div className="placeholder-inner">
                <strong>{activeDataset.label}</strong>
                <p>Gameplay video not bundled yet.</p>
                <p className="muted">
                  Export <code>showcase-session.mp4</code> to{" "}
                  <code>public/demo/{activeDataset.id}/gameplay/</code>
                </p>
                <p>Timeline + reports work offline. Scrub below or press Play.</p>
              </div>
            </div>
          )}

          <div className="transport-row">
            <button type="button" onClick={togglePlay}>
              {isPlaying ? "Pause" : "Play"}
            </button>
            <input
              type="range"
              min={activeDataset.segmentStart || 0}
              max={activeDataset.durationSeconds}
              value={currentSecond}
              onChange={(event) => seekTo(Number(event.target.value))}
              className="scrubber"
            />
          </div>

          <div className="moment-rail">
            {activeDataset.keyMoments?.map((moment) => (
              <button
                key={moment.id}
                type="button"
                className={`moment-chip ${moment.id === selectedMomentId ? "active" : ""} ${moment.severity}`}
                onClick={() => {
                  setSelectedMomentId(moment.id);
                  seekTo(moment.startSeconds);
                }}
              >
                <strong>{moment.title}</strong>
                <span>
                  {moment.startSeconds}s–{moment.endSeconds}s
                </span>
              </button>
            ))}
          </div>

          {selectedMoment ? (
            <div className="moment-detail">
              <p>{selectedMoment.insight}</p>
            </div>
          ) : null}
        </div>

        <div className="emotion-panel panel">
          <div className="panel-head">
            <h3>Emotion signal</h3>
            <span className="privacy-badge">No face video</span>
          </div>

          <div className="emotion-status">
            <span className="emotion-emoji">{quadrant.emoji}</span>
            <div>
              <strong>{quadrant.label}</strong>
              <p>
                {currentEmotion.emotionLabel} · A {currentEmotion.arousal?.toFixed(2)} · V{" "}
                {currentEmotion.valence?.toFixed(2)}
              </p>
            </div>
          </div>

          <VaQuadrant arousal={currentEmotion.arousal} valence={currentEmotion.valence} />

          <div className="chart-wrap">
            <Line
              data={chartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                interaction: { mode: "nearest", axis: "x", intersect: false },
                scales: {
                  x: {
                    ticks: { color: "#9fb0d9", maxTicksLimit: 10 },
                    grid: { color: "rgba(159, 176, 217, 0.12)" },
                  },
                  y: {
                    min: -1,
                    max: 1,
                    ticks: { color: "#9fb0d9" },
                    grid: { color: "rgba(159, 176, 217, 0.12)" },
                  },
                },
                plugins: {
                  legend: { labels: { color: "#eef3ff" } },
                },
                onClick: handleChartClick,
              }}
            />
          </div>
        </div>
      </section>

      <section className="report-gallery panel">
        <div className="panel-head">
          <h3>Studio report outputs</h3>
          <span>{activeDataset.reportPackName}</span>
        </div>

        <div className="report-tabs">
          {availableReportTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`report-tab ${tab.id === activeReportId ? "active" : ""}`}
              onClick={() => setActiveReportId(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeReportUrl ? (
          <iframe
            key={`${activeDatasetId}-${activeReportId}`}
            title={`${activeDataset.label} ${activeReportId}`}
            src={activeReportUrl}
            className="report-frame"
          />
        ) : (
          <p className="muted">Report not available for this game pack.</p>
        )}
      </section>

      <footer className="demo-footer">
        <span>{manifest.privacyNote}</span>
        <span>Offline kiosk · {activeDataset.label}</span>
      </footer>
    </div>
  );
}
